// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/cosmetics/components/RoomOutfitEditor.module.css';
import {RoomOutfitStage} from '@app/features/cosmetics/components/RoomOutfitStage';
import {type CosmeticItem, isRoomPartLocked, type RoomLook} from '@app/features/cosmetics/state/Cosmetics';
import {COSMETIC_TYPE_LABELS} from '@app/features/cosmetics/utils/CosmeticLabels';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import {
	presetOutfit,
	ROOM_PART_MANIFEST,
	type RoomOutfit,
	roomPartPreviewUrl,
	roomPresetPreviewUrl,
	wearRoomPart,
} from '@app/features/cosmetics/utils/RoomOutfitModels';
import {observeIntersection} from '@app/features/platform/utils/SharedIntersectionObserver';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {defaultShirtColor, ROOM_SHIRT_PALETTE} from '@fluxer/constants/src/RoomCharacters';
import {ROOM_OUTFIT_SLOTS, type RoomOutfitSlot, resolveOutfit} from '@fluxer/constants/src/RoomOutfit';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CheckIcon, LockSimpleIcon, ProhibitIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type KeyboardEvent, useEffect, useId, useMemo, useRef, useState} from 'react';

// Tiles drawn at a time; the next chunk comes when the end of the grid nears the view.
const TILE_CHUNK = 48;

const LOOKS_DESCRIPTOR = msg({
	message: 'Ready-made looks',
	comment: '3D room character editor: heading over outfits that fill every slot with one click.',
});
const LOOK_NUMBER_DESCRIPTOR = msg({
	message: 'Ready-made look {number}',
	comment: '3D room character editor: accessible name of one ready-made outfit. {number} counts from 1.',
});
const SLOTS_DESCRIPTOR = msg({
	message: 'Character parts',
	comment: '3D room character editor: accessible name of the slot tabs (face, hair, top and so on).',
});
const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: '3D room character editor: the tile that leaves an optional slot, such as the hat, empty.',
});
const LOCKED_PART_DESCRIPTOR = msg({
	message: '{name}, locked',
	comment: '3D room character editor: accessible name of a part the account cannot wear yet. {name} is the part name.',
});
const CLOTHES_COLOUR_DESCRIPTOR = msg({
	message: 'Clothes colour',
	comment: '3D room character editor: the colour choices for the clothes that take the user colour.',
});
const CLOTHES_COLOUR_NUMBER_DESCRIPTOR = msg({
	message: 'Clothes colour {number}',
	comment: '3D room character editor: accessible name of one clothes colour swatch. {number} is 1 to 16.',
});

// Layouts the stylesheet draws (data-direction): a locker, a mirror, a studio. Production draws the first; preview
// builds get a picker to compare them in place.
const DIRECTIONS = ['a', 'b', 'c'] as const;
type RoomOutfitDirection = (typeof DIRECTIONS)[number];
const DIRECTION_PICKER = import.meta.env.PUBLIC_ROOM_CHARACTER_PREVIEW === '1';

interface RoomOutfitEditorProps {
	look: RoomLook;
	// Room character parts from the store, locked ones included.
	parts: ReadonlyArray<CosmeticItem>;
	// Room characters (ready-made outfits sold whole, keyed by preset), locked ones included.
	characters: ReadonlyArray<CosmeticItem>;
	userId: string;
	isPremium: boolean;
	onChange: (look: RoomLook) => void;
	// A part or look the account cannot wear yet was picked.
	onLocked: (item: CosmeticItem) => void;
}

function toHex(color: number): string {
	return `#${color.toString(16).padStart(6, '0')}`;
}

const DEFAULT_PRESET = ROOM_PART_MANIFEST.families[ROOM_PART_MANIFEST.default_family]!.default_preset;
// The default look first, then the others as the manifest lists them.
const PRESET_KEYS = Object.keys(ROOM_PART_MANIFEST.presets).sort(
	(a, b) => Number(b === DEFAULT_PRESET) - Number(a === DEFAULT_PRESET),
);
// Slots the default look fills; the others can be left empty.
const FILLED_SLOTS = new Set(
	ROOM_PART_MANIFEST.presets[DEFAULT_PRESET]!.parts.map((key) => ROOM_PART_MANIFEST.parts[key]?.slot),
);
const TAB_STEPS: Partial<Record<string, number>> = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1};

function sameOutfit(a: RoomOutfit, b: RoomOutfit): boolean {
	return ROOM_OUTFIT_SLOTS.every((slot) => a[slot] === b[slot]);
}

// The 3D room character editor: a stage, ready-made looks, a tab per slot over a grid of parts, and the clothes
// colour. Controlled: every pick goes out through onChange and nothing is saved here.
export const RoomOutfitEditor = observer(function RoomOutfitEditor({
	look,
	parts,
	characters,
	userId,
	isPremium,
	onChange,
	onLocked,
}: RoomOutfitEditorProps) {
	const {i18n} = useLingui();
	const idPrefix = useId();
	const [slot, setSlot] = useState<RoomOutfitSlot>('top');
	const [direction, setDirection] = useState<RoomOutfitDirection>('a');
	// The part or look the stage shows until the 3D stage draws the whole outfit.
	const [focusKey, setFocusKey] = useState<string | null>(null);
	const tabRefs = useRef(new Map<RoomOutfitSlot, HTMLButtonElement>());
	const byId = useMemo(() => new Map(parts.map((item) => [item.id, item])), [parts]);
	const byKey = useMemo(
		() => new Map(parts.flatMap((item) => (item.model_key ? [[item.model_key, item] as const] : []))),
		[parts],
	);
	const keyOf = (itemId: string) => byId.get(itemId)?.model_key ?? null;
	const wornKeys = ROOM_OUTFIT_SLOTS.flatMap((part) => {
		const key = look.outfit[part] ? keyOf(look.outfit[part]) : null;
		return key ? [key] : [];
	});
	const resolved = resolveOutfit(ROOM_PART_MANIFEST, wornKeys);
	const drawnKeys = new Map(resolved.parts.map((part) => [part.slot, part.key]));
	const selectedKey = (part: RoomOutfitSlot) => keyOf(look.outfit[part] ?? '') ?? drawnKeys.get(part) ?? null;
	const presetItem = (key: string) => {
		const item = byKey.get(key);
		return item ? {id: item.id, wearable: !isRoomPartLocked(item, isPremium)} : null;
	};
	const presets = PRESET_KEYS.map((key) => ({
		key,
		character: characters.find((item) => item.model_key === key) ?? null,
		...presetOutfit(key, presetItem)!,
	}));
	// A look whose parts are not all wearable goes on as its room character when the account may wear that.
	const wholeCharacter = (preset: (typeof presets)[number]) =>
		preset.locked.length > 0 && preset.character && !isRoomPartLocked(preset.character, isPremium)
			? preset.character
			: null;
	const wornPreset =
		look.characterId === null
			? presets.find((preset) => preset.locked.length === 0 && sameOutfit(preset.outfit, look.outfit))
			: presets.find((preset) => preset.character?.id === look.characterId);
	const shirt = look.shirt ?? defaultShirtColor(userId);
	// A worn room character draws its own look in its own colour instead of the stored parts.
	const characterLook = look.characterId === null ? undefined : ROOM_PART_MANIFEST.presets[wornPreset?.key ?? ''];
	const stageParts = characterLook ? resolveOutfit(ROOM_PART_MANIFEST, characterLook.parts).parts : resolved.parts;
	const wear = (outfit: RoomOutfit, focus: string) => {
		setFocusKey(focus);
		onChange({...look, characterId: null, outfit});
	};
	const pickPart = (item: CosmeticItem | null) => {
		if (item && isRoomPartLocked(item, isPremium)) {
			onLocked(item);
			return;
		}
		wear(wearRoomPart(look.outfit, slot, item?.id ?? null, keyOf), item?.model_key ?? '');
	};
	const pickPreset = (preset: (typeof presets)[number]) => {
		const character = wholeCharacter(preset);
		if (character) {
			setFocusKey(preset.key);
			onChange({...look, characterId: character.id});
			return;
		}
		const firstLocked = preset.character ?? preset.locked.map((key) => byKey.get(key)).find((item) => item);
		if (preset.locked.length > 0 && firstLocked) onLocked(firstLocked);
		else if (preset.locked.length === 0) wear(preset.outfit, preset.key);
	};
	const selectTab = (next: RoomOutfitSlot, focus = false) => {
		setSlot(next);
		setFocusKey(null);
		if (focus) tabRefs.current.get(next)?.focus();
	};
	const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
		const count = ROOM_OUTFIT_SLOTS.length;
		const step = TAB_STEPS[event.key];
		const next =
			step !== undefined
				? (ROOM_OUTFIT_SLOTS.indexOf(slot) + step + count) % count
				: event.key === 'Home'
					? 0
					: event.key === 'End'
						? count - 1
						: null;
		if (next === null) return;
		event.preventDefault();
		selectTab(ROOM_OUTFIT_SLOTS[next]!, true);
	};
	// A ready-made look is a full figure; otherwise the last picked part, or the open slot's.
	const lookUrl = focusKey ? roomPresetPreviewUrl(focusKey) : wornPreset ? roomPresetPreviewUrl(wornPreset.key) : null;
	const stageKey = focusKey || selectedKey(slot);
	const partUrl = lookUrl ? null : stageKey ? roomPartPreviewUrl(stageKey) : null;
	const stageImage = lookUrl || partUrl || roomPresetPreviewUrl(DEFAULT_PRESET);
	const stageItem = partUrl && stageKey ? byKey.get(stageKey) : undefined;
	const stageCaption = stageItem ? cosmeticName(stageItem, i18n.locale) : null;
	const slotItems = parts.filter((item) => item.type === `room_${slot}`);
	const panelId = `${idPrefix}-panel`;
	return (
		<div className={styles.root} data-flx="cosmetics.room-outfit-editor.root">
			{DIRECTION_PICKER && (
				<div className={styles.directionPicker} role="group" data-flx="cosmetics.room-outfit-editor.direction-picker">
					{DIRECTIONS.map((entry) => (
						<button
							key={entry}
							type="button"
							aria-pressed={entry === direction}
							onClick={() => setDirection(entry)}
							data-flx="cosmetics.room-outfit-editor.direction"
						>
							{entry.toUpperCase()}
						</button>
					))}
				</div>
			)}
			<div className={styles.editor} data-direction={direction} data-flx="cosmetics.room-outfit-editor.editor">
				<div className={styles.stageColumn} data-flx="cosmetics.room-outfit-editor.stage-column">
					<RoomOutfitStage
						parts={stageParts.map((part) => part.key)}
						shirt={characterLook?.shirt ?? shirt}
						imageUrl={stageImage || null}
						kind={partUrl ? 'part' : 'look'}
						caption={stageCaption}
						data-flx="cosmetics.room-outfit-editor.room-outfit-stage"
					/>
				</div>
				<section
					className={styles.looks}
					aria-labelledby={`${idPrefix}-looks`}
					data-flx="cosmetics.room-outfit-editor.looks"
				>
					<h3 id={`${idPrefix}-looks`} className={styles.heading} data-flx="cosmetics.room-outfit-editor.looks-heading">
						{i18n._(LOOKS_DESCRIPTOR)}
					</h3>
					<div
						className={styles.lookRow}
						role="group"
						aria-labelledby={`${idPrefix}-looks`}
						data-flx="cosmetics.room-outfit-editor.look-row"
					>
						{presets.map((preset, index) => {
							const unavailable = !preset.character && preset.locked.some((key) => !byKey.has(key));
							const locked = preset.locked.length > 0 && !wholeCharacter(preset);
							const selected = preset === wornPreset;
							return (
								<FocusRing key={preset.key} offset={-2} data-flx="cosmetics.room-outfit-editor.look-focus-ring">
									<button
										type="button"
										className={clsx(styles.look, selected && styles.selected)}
										aria-pressed={selected}
										aria-disabled={unavailable || undefined}
										aria-label={i18n._(LOOK_NUMBER_DESCRIPTOR, {number: index + 1})}
										data-room-preset={preset.key}
										onClick={() => pickPreset(preset)}
										data-flx="cosmetics.room-outfit-editor.look"
									>
										<img
											className={styles.lookImage}
											src={roomPresetPreviewUrl(preset.key) ?? undefined}
											alt=""
											loading="lazy"
											draggable={false}
											data-flx="cosmetics.room-outfit-editor.look-image"
										/>
										{locked && !unavailable && <LockBadge data-flx="cosmetics.room-outfit-editor.lock-badge" />}
									</button>
								</FocusRing>
							);
						})}
					</div>
				</section>
				<div className={styles.wardrobe} data-flx="cosmetics.room-outfit-editor.wardrobe">
					<div
						className={styles.tabs}
						role="tablist"
						aria-label={i18n._(SLOTS_DESCRIPTOR)}
						data-flx="cosmetics.room-outfit-editor.tabs"
					>
						{ROOM_OUTFIT_SLOTS.map((part) => {
							const active = part === slot;
							const thumb = roomPartPreviewUrl(selectedKey(part));
							return (
								<FocusRing key={part} offset={-2} data-flx="cosmetics.room-outfit-editor.tab-focus-ring">
									<button
										ref={(element) => {
											if (element) tabRefs.current.set(part, element);
											else tabRefs.current.delete(part);
										}}
										id={`${idPrefix}-tab-${part}`}
										type="button"
										role="tab"
										className={styles.tab}
										aria-selected={active}
										aria-controls={panelId}
										tabIndex={active ? 0 : -1}
										onClick={() => selectTab(part)}
										onKeyDown={handleTabKey}
										data-flx="cosmetics.room-outfit-editor.tab"
									>
										<span className={styles.tabThumb} aria-hidden data-flx="cosmetics.room-outfit-editor.tab-thumb">
											{thumb ? (
												<img
													src={thumb}
													alt=""
													loading="lazy"
													draggable={false}
													data-flx="cosmetics.room-outfit-editor.tab-image"
												/>
											) : (
												<ProhibitIcon weight="bold" data-flx="cosmetics.room-outfit-editor.tab-empty-icon" />
											)}
										</span>
										<span className={styles.tabLabel} data-flx="cosmetics.room-outfit-editor.tab-label">
											{i18n._(COSMETIC_TYPE_LABELS[`room_${part}`])}
										</span>
									</button>
								</FocusRing>
							);
						})}
					</div>
					<PartGrid
						key={slot}
						id={panelId}
						labelledBy={`${idPrefix}-tab-${slot}`}
						items={slotItems}
						withNone={!FILLED_SLOTS.has(slot)}
						selectedKey={selectedKey(slot)}
						isPremium={isPremium}
						onPick={pickPart}
						data-flx="cosmetics.room-outfit-editor.part-grid"
					/>
					<div className={styles.colour} data-flx="cosmetics.room-outfit-editor.colour">
						<h3
							id={`${idPrefix}-colour`}
							className={styles.heading}
							data-flx="cosmetics.room-outfit-editor.colour-heading"
						>
							{i18n._(CLOTHES_COLOUR_DESCRIPTOR)}
						</h3>
						<div
							className={styles.swatches}
							role="group"
							aria-labelledby={`${idPrefix}-colour`}
							data-flx="cosmetics.room-outfit-editor.swatches"
						>
							{ROOM_SHIRT_PALETTE.map((color, index) => (
								<FocusRing key={color} offset={-2} data-flx="cosmetics.room-outfit-editor.swatch-focus-ring">
									<button
										type="button"
										className={clsx(styles.swatch, color === shirt && styles.selected)}
										style={{backgroundColor: toHex(color)}}
										aria-pressed={color === shirt}
										aria-label={i18n._(CLOTHES_COLOUR_NUMBER_DESCRIPTOR, {number: index + 1})}
										onClick={() => onChange({...look, shirt: color})}
										data-flx="cosmetics.room-outfit-editor.swatch"
									/>
								</FocusRing>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

function LockBadge() {
	return (
		<span className={styles.lockBadge} aria-hidden data-flx="cosmetics.room-outfit-editor.lock-badge">
			<LockSimpleIcon weight="fill" data-flx="cosmetics.room-outfit-editor.lock-icon" />
		</span>
	);
}

interface PartGridProps {
	id: string;
	labelledBy: string;
	items: ReadonlyArray<CosmeticItem>;
	withNone: boolean;
	selectedKey: string | null;
	isPremium: boolean;
	onPick: (item: CosmeticItem | null) => void;
}

// A slot can hold hundreds of parts: tiles come in chunks, the next one when a single sentinel after the last tile
// nears the view. Tiles never measure themselves and images load lazily.
function PartGrid({id, labelledBy, items, withNone, selectedKey, isPremium, onPick}: PartGridProps) {
	const {i18n} = useLingui();
	const [shown, setShown] = useState(TILE_CHUNK);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const more = shown < items.length;
	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !more) return;
		return observeIntersection(
			sentinel,
			(entry) => {
				if (entry.isIntersecting) setShown((count) => count + TILE_CHUNK);
			},
			{rootMargin: '0px 0px 400px 0px'},
		);
	}, [more, shown]);
	return (
		<div
			id={id}
			className={styles.grid}
			role="tabpanel"
			aria-labelledby={labelledBy}
			data-flx="cosmetics.room-outfit-editor.grid"
		>
			{withNone && (
				<PartTile
					label={i18n._(NONE_DESCRIPTOR)}
					imageUrl={null}
					selected={selectedKey === null}
					locked={false}
					onClick={() => onPick(null)}
					data-flx="cosmetics.room-outfit-editor.none-tile"
				/>
			)}
			{items.slice(0, shown).map((item) => {
				const locked = isRoomPartLocked(item, isPremium);
				return (
					<PartTile
						key={item.id}
						label={
							locked
								? i18n._(LOCKED_PART_DESCRIPTOR, {name: cosmeticName(item, i18n.locale)})
								: cosmeticName(item, i18n.locale)
						}
						imageUrl={roomPartPreviewUrl(item.model_key)}
						selected={item.model_key === selectedKey}
						locked={locked}
						onClick={() => onPick(item)}
						data-flx="cosmetics.room-outfit-editor.part-tile"
					/>
				);
			})}
			{more && <div ref={sentinelRef} className={styles.sentinel} data-flx="cosmetics.room-outfit-editor.sentinel" />}
		</div>
	);
}

interface PartTileProps {
	label: string;
	imageUrl: string | null;
	selected: boolean;
	locked: boolean;
	onClick: () => void;
}

function PartTile({label, imageUrl, selected, locked, onClick}: PartTileProps) {
	return (
		<FocusRing offset={-2} data-flx="cosmetics.room-outfit-editor.tile-focus-ring">
			<button
				type="button"
				className={clsx(styles.tile, selected && styles.selected, locked && styles.locked)}
				aria-pressed={selected}
				aria-label={label}
				title={label}
				onClick={onClick}
				data-flx="cosmetics.room-outfit-editor.tile"
			>
				{imageUrl ? (
					<img
						className={styles.tileImage}
						src={imageUrl}
						alt=""
						loading="lazy"
						decoding="async"
						draggable={false}
						data-flx="cosmetics.room-outfit-editor.tile-image"
					/>
				) : (
					<ProhibitIcon className={styles.noneIcon} weight="bold" data-flx="cosmetics.room-outfit-editor.none-icon" />
				)}
				{selected && (
					<span className={styles.checkBadge} aria-hidden data-flx="cosmetics.room-outfit-editor.check-badge">
						<CheckIcon weight="bold" data-flx="cosmetics.room-outfit-editor.check-icon" />
					</span>
				)}
				{locked && <LockBadge data-flx="cosmetics.room-outfit-editor.lock-badge" />}
			</button>
		</FocusRing>
	);
}
